import i18n from '../../../core/i18n';
import services from '../../../core/services';
import { downloadWithLink } from '../../common/helpers/downloadHelper';
import { BulkActionService } from '../../common/services/bulkActionService';
import filesApi from '../api/files';
import damFileDownloadNotification from '../components/files/download/FileDownloadNotification';
import { DownloadStates } from '../constants/file';

export class FileDownloadManager {
  constructor() {
    /**
     * The notification for the file download
     */
    this.notificationId = -1;
  }
  /**
   * Downloads the files and manages the notifications and alerts
   *
   * @param {string[]} fileIds - array of files/folders pairs to associate
   * @param {string|null} taskName - the task to execute the download
   * @returns {Promise<void>}
   */
  static async downloadFiles(fileIds, taskName) {
    if (!fileIds.length) {
      return Promise.resolve();
    }

    let fileDownloadManager;

    try {
      fileDownloadManager = new FileDownloadManager();
      await fileDownloadManager.init();
      services.getService('notifications').openNotifications();

      const triggerBulkDownloadRes = await filesApi.downloadFiles(fileIds, taskName);

      BulkActionService.pollBulkAction(triggerBulkDownloadRes.body.bulkActionId, {
        onStatusFinished: fileDownloadManager.onBulkDownloadReady.bind(fileDownloadManager),
        onStatusInError: fileDownloadManager.onDownloadPreparationError.bind(fileDownloadManager),
      });
    } catch (err) {
      if (fileDownloadManager) {
        fileDownloadManager.onDownloadError(err);
      }
    }
  }

  /**
   * Create the initial notification for a download
   *
   * @returns {Promise<void>}
   */
  async init() {
    this.notificationId = await services
      .getService('notifications')
      .createNotification(damFileDownloadNotification, {
        state: DownloadStates.DOWNLOADING,
      });
  }

  /**
   * Called when the the files are ready to download.
   * Triggers an actual download of the files
   *
   * @returns {Promise<void>}
   */
  onBulkDownloadReady(bulkDetails) {
    try {
      const downloadUrl = bulkDetails.executionResult ? bulkDetails.executionResult.result : null;

      services.getService('notifications').updateNotification(this.notificationId, {
        state: DownloadStates.READY,
        downloadUrl,
      });

      if (downloadUrl) {
        downloadWithLink(downloadUrl);
      } else {
        throw new Error('Download url is empty or null');
      }
    } catch (error) {
      this.onDownloadError(error);
    }
  }

  /**
   * Called when the bulk preparation of the download fails
   *
   * @param {object} bulkDetails - the details of the bulk download preparation
   */
  onDownloadPreparationError(bulkDetails) {
    this.onDownloadError(new Error('Bulk download preparation finished in error'));
  }

  /**
   * Called when any error in the download process occurs
   *
   * @param {Error} err - the error that caused the failure
   * @returns {void}
   */
  // eslint-disable-next-line handle-callback-err
  onDownloadError(err) {
    services.getService('notifications').updateNotification(this.notificationId, {
      state: DownloadStates.ERROR,
    });

    services.getService('alerts').alertError(i18n.t('dam.files.download.alert.error'));
  }
}
